import {
  Link,
  createFileRoute,
  stripSearchParams,
} from "@tanstack/react-router";
import { CheckCircle } from "lucide-react";
import { Suspense, useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { z } from "zod/v4";
import type { ComponentProps } from "react";
import type { PaymentPeriod } from "@/lib/types/payment";
import type { Plan } from "@/lib/queries/payment/types";
import type { Prettify } from "@/lib/types";
import type { ModelCoefficients } from "@/lib/queries/modelCoefficients";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { plansQueryOptions } from "@/lib/queries/payment/plans";
import { LoadingSpinner } from "@/components/loading";
import { api } from "@/lib/http";
import {
  useUserPlan,
  userPlanQueryOptions,
} from "@/lib/queries/payment/userPlan";

import {
  apiSubscriptionQueryOptions,
  useApiSubscription,
} from "@/lib/queries/payment/apiSubscription";
import {
  apiPlansQueryOptions,
  useApiPlans,
} from "@/lib/queries/payment/apiPlans";
import { modelCoefficientsQueryOptions } from "@/lib/queries/modelCoefficients";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { searchBoolean } from "@/lib/utils/zod";
import { useCouponCheck } from "@/lib/hooks/useCouponCheck";
import { CouponBanner } from "@/components/ui/coupon-banner";
import { useBuyPlan } from "@/lib/mutations/plan";

export const pricingPageSearchSchema = z.object({
  // have to do it like this to not require to specify the default again on every link going to here
  // if such a thing is needed in a bunch of places I'll make a helper fn
  tab: z
    .enum(["ai-detector", "stealth-ai", "api-agent"])
    .optional()
    .default("ai-detector")
    .catch("ai-detector"),
  period: z
    .enum(["yearly", "monthly"])
    .optional()
    .default("yearly")
    .catch("yearly"),

  freePlanExpired: searchBoolean,

  coupon: z.string().optional(),
});

export const Route = createFileRoute("/_app/pricing")({
  validateSearch: pricingPageSearchSchema,
  search: {
    middlewares: [
      stripSearchParams({
        freePlanExpired: "false",
      }),
    ],
  },
  component: RouteComponent,
  loader({ context: { queryClient } }) {
    [
      userPlanQueryOptions,
      plansQueryOptions,
      apiSubscriptionQueryOptions,
      apiPlansQueryOptions,
      modelCoefficientsQueryOptions,
    ].map((q) => queryClient.ensureQueryData(q as any));
  },
  pendingComponent: LoadingSpinner,

  head: () => ({
    meta: [
      {
        title: "Pricing | Polygraf AI – Transparent Enterprise AI Plans",
      },
      {
        name: "description",
        content:
          "Compare Polygraf AI plans - core detection to full governance - usage pricing, no hidden fees, deploy in local or on-premises environments.",
      },
    ],
  }),
});

// Animated button component for sign in/purchase text switching
function AnimatedSignInButton({
  className,
  to,
  search,
  disabled,
  buyPlan,
  isCurrentPlan,
  displayPrice,
  isLoggedOut,
}: {
  className?: string;
  to: string;
  search: (s: any) => any;
  disabled?: boolean;
  buyPlan: () => void;
  isCurrentPlan: boolean;
  displayPrice: PriceDisplay;
  isLoggedOut: boolean;
}) {
  const [currentText, setCurrentText] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const texts = isLoggedOut
    ? ["Sign in / Sign up", "Get Now"]
    : ["Get Started", "Get Now"];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsFading(true);

      setTimeout(() => {
        setCurrentText((prev) => (prev + 1) % texts.length);
      }, 300);

      setTimeout(() => {
        setIsFading(false);
      }, 600);
    }, 3500);

    return () => clearInterval(interval);
  }, [texts.length]);

  const isPurchaseNow = currentText === 1;

  if (isLoggedOut) {
    return (
      <Link
        className={cn(
          buttonVariants({
            variant: "outline",
          }),
          "h-12 w-full rounded-lg py-3 text-[16px] font-medium transition-all duration-500",
          isPurchaseNow
            ? "border-gray-700 bg-gray-700 text-white hover:bg-gray-600 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
            : "border border-gray-700 text-black disabled:border-gray-500 disabled:text-foreground/100 dark:text-foreground/80",
          className,
        )}
        to={to}
        search={search}
        disabled={disabled}
        style={{
          animation: isFading ? "fadeInOut 0.6s ease-in-out" : undefined,
        }}
      >
        {texts[currentText]}
      </Link>
    );
  } else {
    return (
      <Button
        variant="outline"
        className={cn(
          buttonVariants({
            variant: "outline",
          }),
          "h-12 w-full rounded-lg py-3 text-[16px] font-medium transition-all duration-500",
          isPurchaseNow &&
            !isCurrentPlan &&
            displayPrice.type === "number" &&
            displayPrice.value > 0
            ? "border-gray-700 bg-gray-700 text-white hover:bg-gray-600 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
            : "border border-gray-700 text-black disabled:border-gray-500 disabled:text-foreground/100 dark:text-foreground/80",
          className,
        )}
        onClick={buyPlan}
        disabled={disabled}
      >
        {isCurrentPlan
          ? "Current plan"
          : displayPrice.type === "number" && displayPrice.value > 0
            ? texts[currentText]
            : "Not Available"}
      </Button>
    );
  }
}

const featureNameMap = {
  AI_DETECTION: "AI Detector",
  SOURCE_DETECTION: "Source Detector",
  WRITING_ANALYSIS: "Writing Analysis",
  AI_DETECTION_HIGHLIGHTER: "AI Highlighter",
  PLAGIARISM: "Advanced Copyright Detector",
  REWRITE: "Content Rewriter",
  SUMMARIZE: "Content Summarizer",
  EXPAND: "Content Expander",
  SHORTEN: "Content Shortener",
  ADD_CONTENT: "Context Adder",
  HUMANIZER: "AI Humanizer (Beta)",
  PII_TEXT_DETECT: "PII Detector",

  // enterprise mock fields
  ENTERPRISE_Deception_Filter: "Deception Filter",
  ENTERPRISE_Uploading_Documents: "Uploading Documents",
  ENTERPRISE_Image_Detector_OCR: "Image Detector (OCR)",
} as const;

function RouteComponent() {
  const tab = Route.useSearch({ select: (s) => s.tab });
  // const navigate = Route.useNavigate();
  // const setTab = useCallback(
  //   (newTab: typeof tab) =>
  //     navigate({
  //       search: (old) => ({ ...old, tab: newTab }),
  //     }),
  //   [navigate],
  // );

  const currentPlan = useUserPlan();
  const hasExpiredFreePlan =
    currentPlan.isSuccess && currentPlan.data.status === "free_plan_expired";
  const hasForeignPlan =
    !currentPlan.isSuccess ||
    (currentPlan.data.status === "ok" &&
      currentPlan.data.info.source !== "Polygraf");

  // TODO: fix this thing to actually do this properly
  // Use the coupon check hook
  const coupon = useCouponCheck();

  return (
    <>
      <FreePlanExpiredModal />
      <div className="px-4 py-16">
        <div className="mb-8 text-center">
          <h1 className="mb-4 text-5xl font-medium">
            Simple, Transparent Pricing.
          </h1>
          <p className="mx-auto max-w-3xl text-xl text-foreground/80">
            Experience Polygraf AI - Clarity within Chaos® in every decision.
            Detect, verify, and trust content with confidence.
          </p>
          {hasForeignPlan && (
            <h6 className="mx-auto mt-2 text-destructive-foreground">
              Your subscription is handled by {currentPlan.data?.info?.source}{" "}
              and cannot be cancelled or changed through this website
            </h6>
          )}
          {hasExpiredFreePlan && (
            <h6 className="mx-auto mt-2 text-destructive-foreground">
              Your Free Plan has expired and you must purchase a new plan to
              continue using Polygraf AI
            </h6>
          )}

          {/* Coupon Banner */}
          {coupon.code && !coupon.query.isLoading && (
            <div className="mx-auto mt-4 max-w-2xl">
              <CouponBanner
                isValid={coupon.isValid}
                couponCode={coupon.code}
                couponName={coupon.query.data?.coupon?.name}
                discountInfo={coupon.discountInfo}
              />
            </div>
          )}
        </div>

        <div className="mb-8 flex items-center justify-center">
          <Tabs defaultValue={tab} className="flex items-center justify-center">
            {/* <TabsList className="mb-4 grid h-12 grid-cols-1 rounded-lg bg-gray-100 p-1 dark:bg-transparent">
              <TabsTrigger
                value="ai-detector"
                onClick={() => setTab("ai-detector")}
                className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                <span className="flex items-center gap-2">
                  <img src="/icons/stars-icon.svg" alt="Stars Icon" />
                  AI Detector
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="stealth-ai"
                onClick={() => setTab("stealth-ai")}
                className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                <span className="flex items-center gap-2">
                  <img src="/icons/shuriken-icon.svg" alt="Shuriken Icon" />
                  Stealth AI
                </span>
              </TabsTrigger>
              <TabsTrigger
                value="api-agent"
                onClick={() => setTab("api-agent")}
                className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
              >
                <span className="flex items-center gap-2">
                  <img src="/icons/link.svg" alt="Link Icon" />
                  API Agent
                </span>
              </TabsTrigger>
            </TabsList> */}
            <Suspense fallback={<LoadingSpinner />}>
              <TabsContent value="ai-detector">
                <DetectorPricing />
              </TabsContent>
              {/* <TabsContent value="stealth-ai">
                <StealthPricing />
              </TabsContent> */}
              {/* <TabsContent value="api-agent">
                <ApiAgentPricing />
              </TabsContent> */}
            </Suspense>
          </Tabs>
        </div>
      </div>
    </>
  );
}

const enterprisePlanMock: ComponentProps<typeof PlanCard>["plan"] = {
  id: "enterprise_plan_fake_id",
  name: "Enterprise",
  description: " Advanced features + unlimited users.",
  price: "Custom",
  features: {
    services: {},
    total_token_count: 0,
    available_services: [
      // "AI_DETECTION",
      // "AI_DETECTION_HIGHLIGHTER",
      // "SOURCE_DETECTION",
      // "PLAGIARISM",
      // "ENTERPRISE_Deception_Filter",
      // "ENTERPRISE_Uploading_Documents",
      // "ENTERPRISE_Image_Detector_OCR",
    ] satisfies Array<keyof typeof featureNameMap>,
  },
  is_enterprise: true,
};

export function DetectorPricing() {
  const period = Route.useSearch({
    select: (s) => s.period,
  });
  const navigate = Route.useNavigate();
  const setPeriod = useCallback(
    (newPeriod: typeof period) =>
      navigate({ search: (old) => ({ ...old, period: newPeriod }) }),
    [navigate],
  );

  const plans = useSuspenseQuery({
    ...plansQueryOptions,
    select({ data }) {
      return data.filter(
        ({ name }) =>
          !name.toLowerCase().includes("stealth") &&
          !name.toLowerCase().includes("standard"),
      );
    },
  });

  const [clickedPlan, setClickedPlan] = useState<string | null>(
    plans.data.find(({ name }) => name.toLowerCase().includes("premium"))?.id ||
      null,
  );

  return (
    <>
      <div className="mb-12 flex justify-center">
        <Tabs defaultValue={period}>
          <TabsList className="grid h-12 grid-cols-2 rounded-lg bg-gray-100 p-1 dark:bg-transparent">
            <TabsTrigger
              value="monthly"
              onClick={() => setPeriod("monthly")}
              className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <span className="flex items-center gap-2">Monthly</span>
            </TabsTrigger>
            <TabsTrigger
              value="yearly"
              onClick={() => setPeriod("yearly")}
              className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <span className="flex items-center gap-2">Yearly</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-4">
        {plans.data.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            period={period}
            setClickedPlan={setClickedPlan}
            clickedPlan={clickedPlan}
          />
        ))}

        <ApiAgentCard
          setClickedPlan={setClickedPlan}
          clickedPlan={clickedPlan}
        />

        <PlanCard
          plan={enterprisePlanMock}
          period={period}
          variant="contact"
          setClickedPlan={setClickedPlan}
          clickedPlan={clickedPlan}
        />
      </div>
      <div className="m-2 mt-12 w-full">
        <DetectorTokenApproximations />
      </div>
    </>
  );
}

const tokenApproximationKeys = {
  SOURCE_DETECTION: "AI Detector",
  AI_DETECTION_HIGHLIGHTER: "AI Highlighter",
  QUILLBOT_ANALYSIS: "Humanized Text Detector",
  PLAGIARISM: "Copyright Detector",
  WRITING_ANALYSIS: "Writing Analysis",
  HUMANIZER: "AI Humanizer (Beta)",
  SUMMARIZE: "Summarizer",
} as const satisfies Partial<Record<keyof ModelCoefficients, string>>;
const tokenApproximationPerHundred = 120;

function DetectorTokenApproximations() {
  const coefficients = useQuery(modelCoefficientsQueryOptions);
  return (
    <>
      <h2 className="mb-2 text-xl font-bold">
        Approximate AI Agent token usage per 100 words:
      </h2>
      <div className="flex flex-row flex-wrap justify-between gap-x-3">
        {Object.entries(tokenApproximationKeys).map(([key, title]) => (
          <div className="flex max-h-6 w-[40%] min-w-75 flex-row items-center">
            <div className="self-start">{title}:</div>{" "}
            <div className="ml-auto flex flex-row items-center gap-1 self-end font-bold text-primary">
              ≈
              {coefficients.isSuccess
                ? coefficients.data[
                    key as keyof typeof tokenApproximationKeys
                  ] * tokenApproximationPerHundred
                : "..."}
              <img
                src="/icons/p-token.svg"
                alt="Token Icon"
                className="size-4"
              />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export function StealthPricing() {
  const period = Route.useSearch({
    select: (s) => s.period,
  });
  const navigate = Route.useNavigate();
  const setPeriod = useCallback(
    (newPeriod: typeof period) =>
      navigate({ search: (old) => ({ ...old, period: newPeriod }) }),
    [navigate],
  );

  const plans = useSuspenseQuery({
    ...plansQueryOptions,
    select: ({ data }) => {
      return data.filter(({ name }) => name.toLowerCase().includes("stealth"));
    },
  });

  const [clickedPlan, setClickedPlan] = useState<string | null>(null);

  return (
    <>
      <div className="mb-12 flex justify-center">
        <Tabs defaultValue={period}>
          <TabsList className="grid h-12 grid-cols-2 rounded-lg bg-gray-100 p-1 dark:bg-transparent">
            <TabsTrigger
              value="monthly"
              onClick={() => setPeriod("monthly")}
              className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <span className="flex items-center gap-2">Monthly</span>
            </TabsTrigger>
            <TabsTrigger
              value="yearly"
              onClick={() => setPeriod("yearly")}
              className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm"
            >
              <span className="flex items-center gap-2">Yearly</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {plans.data.map((plan) => (
        <PlanCard
          key={plan.id}
          plan={plan}
          period={period}
          setClickedPlan={setClickedPlan}
          clickedPlan={clickedPlan}
        />
      ))}
    </>
  );
}
// export function ApiAgentPricing() {

// const apiPlans = useApiPlans();
// const plans = apiPlans.data ?? [];

// const apiSubscription = useApiSubscription();
// const currentPlan = apiSubscription.data;

// const signedIn = useQuery({
//   ...userInfoQueryOptions,
//   select(data) {
//     return data.state === "logged_in";
//   },
// });

// const buyPlanCheckout = useMutation({
//   mutationFn: async (planId: string) => {
//     return await api.payment
//       .post(`v1/api-services/subscriptions/create-checkout/${planId}`, {})
//       .json<{ checkout_url: string }>();
//   },
//   onSuccess({ checkout_url }) {
//     window.location.href = checkout_url;
//   },
//   onError(error) {
//     console.log(error);
//   },
// });

// // Function to handle the buy plan logic
// const handleBuyPlan = () => {
//   const planDetails = plans;

//   const planId = planDetails[0]?.id;
//   if (!planId) return;

//   buyPlanCheckout.mutate(planId);
// };
// console.log(currentPlan?.status, "12121212");

// return (
//   <div className="flex flex-1 flex-col gap-6">
//     <div className="flex flex-col gap-2 text-center">
//       <h1 className="text-2xl font-bold">Pay Only For What You Need</h1>
//       <p className="text-sm text-gray-500">
//         Scale usage as you grow. Start free, upgrade anytime.
//       </p>
//     </div>

//     <div className="flex w-full flex-col rounded-lg border p-4">
//       <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
//         <div>
//           <h3 className="text-xl font-bold">Base</h3>
//           <p className="text-gray-500">Subscription Fee</p>
//         </div>
//         <div className="text-right md:text-left">
//           <p className="text-xl font-bold">${plans[0]?.price}</p>
//           <p className="text-sm text-gray-500">per user/month</p>
//           <p className="text-xs text-gray-500">
//             After subscribing benefit from a fixed rate based on your usage
//           </p>
//         </div>
//       </div>

//       {/* Divider */}
//       <div className="my-4 h-px bg-gray-200 dark:bg-gray-700" />

//       <div className="relative grid grid-cols-1 gap-6 md:grid-cols-2">
//         {/* Standard Privacy */}
//         <div className="flex flex-col gap-2">
//           <h4 className="text-sm text-gray-500">Standard Privacy API*</h4>
//           <p className="text-base font-semibold">Text Query API</p>
//           <p className="text-base font-semibold">Document Query API</p>
//           <p className="mt-2 text-sm text-gray-500">
//             <span className="font-bold text-foreground">$10</span> per 1
//             million tokens
//           </p>
//           <p className="text-sm text-gray-500">
//             <span className="font-bold text-foreground">$10</span> per 100
//             pages
//           </p>
//         </div>

//         {/* Contextual Privacy */}
//         <div className="flex flex-col gap-2">
//           <h4 className="text-sm text-gray-500">Contextual Privacy API**</h4>
//           <p className="text-base font-semibold">Contextual Text API</p>
//           <p className="text-base font-semibold">Contextual Document API</p>
//           <p className="mt-2 text-sm text-gray-500">
//             <span className="font-bold text-foreground">$15</span> per 1
//             million tokens
//           </p>
//           <p className="text-sm text-gray-500">
//             <span className="font-bold text-foreground">$15</span> per 100
//             pages
//           </p>
//         </div>

//         <div className="hidden bg-gray-200 md:absolute md:inset-y-0 md:left-1/2 md:block md:w-px dark:bg-gray-700" />
//       </div>

//       {/* Buy Now Butonu */}
//       <div className="flex justify-center pt-6">
//         {signedIn.data ? (
//           <Button
//             className="flex items-center gap-2 px-6"
//             onClick={() => {
//               if (currentPlan?.status === "CANCELED" || !currentPlan) {
//                 handleBuyPlan();
//               }
//             }}
//           >
//             {currentPlan?.status === "ACTIVE" ? (
//               "Current Plan"
//             ) : currentPlan?.status === "CANCELED" ? (
//               <span>
//                 Current Plan{" "}
//                 <span>
//                   {`(cancels on ${
//                     currentPlan.end_date
//                       ? new Date(currentPlan.end_date).toLocaleDateString(
//                           "en-US",
//                           {
//                             month: "short",
//                             day: "numeric",
//                             year: "numeric",
//                           },
//                         )
//                       : ""
//                   })`}
//                 </span>
//               </span>
//             ) : (
//               <>
//                 Buy now
//                 <ArrowUpRight className="h-4 w-4" />
//               </>
//             )}
//           </Button>
//         ) : (
//           <Link
//             className={cn(buttonVariants(), "flex items-center gap-2 px-6")}
//             to="/auth/signin"
//             search={(s) => ({
//               redirect: "/pricing",
//               redirectSearch: s,
//             })}
//           >
//             Sign In to Buy
//           </Link>
//         )}
//       </div>
//     </div>

//     <div className="w-full rounded-lg border p-4">
//       <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
//         <div>
//           <h3 className="text-lg font-medium">Standard Privacy API*</h3>
//           <p className="text-sm leading-relaxed text-gray-500">
//             Essential privacy protection for standard use, with the ability to
//             detect and redact over 35 types of PII, PCI, PHI, and
//             SPI—including names, emails, phone numbers, and other sensitive
//             identifiers.
//           </p>
//         </div>
//         <div>
//           <h3 className="text-lg font-medium">Contextual Privacy API**</h3>
//           <p className="text-sm leading-relaxed text-gray-500">
//             Comprehensive privacy protection, securing PII, PCI, PHI, and SPI
//             along with biometric, financial, and geolocation data. Includes
//             contextual detection, advanced document analysis, and customizable
//             redaction rules.
//           </p>
//         </div>
//       </div>

//       {/* Contact Button */}
//       <div className="mt-6 flex justify-center">
//         <Button className="flex items-center gap-2 px-6" asChild>
//           <a
//             href="https://polygraf.ai/contact"
//             target="_blank"
//             rel="noopener noreferrer"
//           >
//             Have questions? Contact us
//             <ArrowUpRight className="h-4 w-4" />
//           </a>
//         </Button>
//       </div>
//     </div>
//   </div>
// );
// }

// API Agent Card Component
function ApiAgentCard({
  setClickedPlan,
  clickedPlan,
}: {
  setClickedPlan: (id: string) => void;
  clickedPlan: string | null;
}) {
  const apiPlans = useApiPlans();
  const plans = apiPlans.data ?? [];
  const apiSubscription = useApiSubscription();
  const currentPlan = apiSubscription.data;

  const currentPlan_user = useUserPlan();
  const isLoggedOut = currentPlan_user.data?.status === "logged_out";

  const buyPlanCheckout = useMutation({
    mutationFn: async (planId: string) => {
      return await api.payment
        .post(`v1/api-services/subscriptions/create-checkout/${planId}`, {})
        .json<{ checkout_url: string }>();
    },
    onSuccess({ checkout_url }) {
      window.location.href = checkout_url;
    },
    onError(error) {
      console.log(error);
    },
  });

  const handleBuyPlan = () => {
    const planId = plans[0]?.id;
    if (!planId) return;
    buyPlanCheckout.mutate(planId);
  };

  const isCurrentPlan = currentPlan?.status === "ACTIVE";
  const isCanceled = currentPlan?.status === "CANCELED";

  return (
    <div
      className={cn(
        "flex h-full flex-col rounded-xl border p-3",
        clickedPlan === "api-agent" && "border-2 border-gray-700",
      )}
      onClick={() => setClickedPlan("api-agent")}
    >
      <div className="mb-4">
        <h3 className="mb-4 text-xl font-medium">API Agent</h3>
        <div className="mb-6 flex gap-1">
          <div className="flex flex-col">
            <h2 className="text-3xl font-bold">${plans[0]?.price || "5"}</h2>
            <h4 className="invisible block text-xl font-bold">
              ${plans[0]?.price || "5"}
            </h4>
          </div>
          <p className="flex flex-col text-sm text-foreground/80">
            <span>per month</span>
            <span>base subscription fee</span>
          </p>
        </div>

        {isLoggedOut ? (
          <Link
            className={cn(
              buttonVariants({
                variant: "outline",
              }),
              "h-12 w-full rounded-lg border border-gray-700 py-3 text-[16px] font-medium text-black dark:text-foreground/80",
            )}
            to="/auth/signin"
            search={(s) => ({
              redirect: "/pricing",
              redirectSearch: s,
            })}
          >
            Get Now
          </Link>
        ) : (
          <Button
            variant="outline"
            className={cn(
              "h-12 w-full rounded-lg py-3 text-[16px] font-medium",
              !isCurrentPlan && !isCanceled
                ? "border-gray-700 bg-gray-700 text-white hover:bg-gray-600 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                : "border border-gray-700 text-black disabled:border-gray-500 disabled:text-foreground/100 dark:text-foreground/80",
            )}
            onClick={handleBuyPlan}
            disabled={isCurrentPlan || buyPlanCheckout.isPending}
          >
            {isCurrentPlan
              ? "Current plan"
              : isCanceled
                ? `Cancels on ${
                    currentPlan.end_date
                      ? new Date(currentPlan.end_date).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          },
                        )
                      : ""
                  }`
                : "Get Now"}
          </Button>
        )}
      </div>

      <div className="flex-1 space-y-6 text-sm">
        {/* Standard Privacy API */}
        <div>
          <h4 className="mt-4 mb-2 text-[16px] font-semibold text-gray-500">
            Standard Privacy API*
          </h4>
          <p className="mb-1 font-semibold">Text Query API</p>
          <p className="mb-2 font-semibold">Document Query API</p>
          <p className="text-gray-500">
            <span className="font-bold text-foreground">$10</span> per 1 million
            tokens
          </p>
          <p className="text-gray-500">
            <span className="font-bold text-foreground">$10</span> per 100 pages
          </p>
        </div>

        {/* Contextual Privacy API */}
        <div>
          <h4 className="mb-2 text-[16px] font-semibold text-gray-500">
            Contextual Privacy API**
          </h4>
          <p className="mb-1 font-semibold">Contextual Text API</p>
          <p className="mb-2 font-semibold">Contextual Document API</p>
          <p className="text-gray-500">
            <span className="font-bold text-foreground">$15</span> per 1 million
            tokens
          </p>
          <p className="text-gray-500">
            <span className="font-bold text-foreground">$15</span> per 100 pages
          </p>
        </div>
      </div>
    </div>
  );
}

type PlanCardPlan = Prettify<
  Pick<
    Plan,
    "id" | "name" | "price" | "description" | "features" | "is_enterprise"
  >
>;

type PriceDisplay =
  | {
      type: "text";
      label: string;
    }
  | {
      type: "number";
      value: number;
    };

const planDetails = [
  {
    name: "Free Plan",
    tokens: "2.5K",
    words: "~2K words",
    features: [
      "AI Detector",
      "Source Detector",
      "Writing Analysis",
      "AI Highlighter",
      "Cognitive Flow Index",
      "Secure LLM"
    ],
  },
  // {
  //   name: "Standard Plan",
  //   tokens: "150K",
  //   words: "~135K words",
  //   features: [
  //     "AI Detector",
  //     "Source Detector",
  //     "Writing Analysis",
  //     "AI Highlighter",
  //     "AI Humanizer (Beta)",
  //     "Cognitive Flow Index",
  //   ],
  // },
  {
    name: "Premium Plan",
    tokens: "1M",
    words: "~900K words",
    features: [
      "AI Detector",
      "Source Detector",
      "Writing Analysis",
      "AI Highlighter",
      "Advanced Copyright Detector",
      "Generate 2 articles a week",
      "Content Modification (Rewrite, Shorten, Summarize, Add)",
      "AI Humanizer (Beta)",
      "Reference Generator",
      "PII Detector",
      "Cognitive Flow Index",
      "Secure LLM"
    ],
  },
  {
    name: "Stealth Writer Plan",
    features: [
      "Content Writer",
      "AI Detector",
      "Source Detector",
      "Writing Analysis",
      // "AI Highlighter",
      "Advanced Copyright Detector",
      "Content Modification (Rewrite, Shorten, Summarize, Add)",
      "Reference Generator",
      "AI Humanizer (Beta)",
      "PII Detector",
      "Download & Export Content",
      "Cognitive Flow Index",
      "Secure LLM"
    ],
  },
];
export function PlanCard({
  plan,
  period,
  variant = "default",
  setClickedPlan,
  clickedPlan,
}: {
  plan: PlanCardPlan;
  period: PaymentPeriod;
  variant?: "default" | "contact";
  setClickedPlan: (id: string) => void;
  clickedPlan: string | null;
}) {
  const coupon = useCouponCheck();

  const parsedPrice = Number(plan.price);
  const isFree = parsedPrice == 0;
  const planDetail = planDetails.find(({ name }) =>
    name.toLowerCase().includes(plan.name.toLowerCase()),
  );

  const currentPlan = useUserPlan();
  const isLoggedOut = currentPlan.data?.status === "logged_out";
  const isCurrentPlan =
    currentPlan.data?.status === "ok" &&
    currentPlan.data.info.plan.id === plan.id &&
    (isFree || currentPlan.data.info.subscription_type === period);

  const hasForeignPlan =
    currentPlan.isSuccess &&
    currentPlan.data.status === "ok" &&
    currentPlan.data.info.source !== "Polygraf";

  const isPlanEligible = coupon.isPlanEligible(plan.id);

  const basePrice =
    period === "monthly" ? parsedPrice : (parsedPrice * 10) / 12;

  let discountedPrice = basePrice;
  if (isPlanEligible && coupon.query.data?.coupon) {
    const { coupon: couponData } = coupon.query.data;
    if (couponData.percent_off) {
      discountedPrice = basePrice * (1 - couponData.percent_off / 100);
    } else if (couponData.amount_off) {
      discountedPrice = Math.max(0, basePrice - couponData.amount_off);
    }
  }

  const displayPrice: PriceDisplay = Number.isNaN(parsedPrice)
    ? {
        type: "text",
        label: plan.price,
      }
    : isFree
      ? { type: "text", label: "Free" }
      : {
          type: "number",
          value: discountedPrice,
        };

  const buyPlan = useBuyPlan({
    id: plan.id,
    period,
    coupon: coupon.code ?? undefined,
  });

  const isPremiumPlan = plan.name.toLowerCase().includes("premium");

  return (
    <div
      className={cn(
        "relative flex h-full flex-col overflow-visible rounded-xl border p-3",
        clickedPlan == plan.id && "border-2 border-gray-700",
      )}
      onClick={() => setClickedPlan(plan.id)}
    >
      <div className="mb-8">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-xl font-medium">
            {plan.name.replace("Plan", "")}
          </h3>
          {isPremiumPlan && (
            <span className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-black">
              Popular
            </span>
          )}
        </div>
        <div className="mb-4">
          <div className="mb-6 flex items-center gap-2">
            {displayPrice.type === "text" ? (
              <h2 className="text-4xl font-bold">
                {plan.name === "Free Plan" ? " " : displayPrice.label}
              </h2>
            ) : (
              <div className="flex gap-2">
                <div className="flex flex-col">
                  {isPlanEligible && basePrice !== discountedPrice ? (
                    <>
                      <div className="flex flex-col gap-1">
                        <h2 className="text-3xl font-bold">
                          $
                          {period === "yearly"
                            ? (displayPrice.value * 12).toFixed(2)
                            : displayPrice.value.toFixed(2)}
                        </h2>
                        <div className="flex gap-2">
                          <span className="text-lg text-gray-500 line-through">
                            $
                            {period === "yearly"
                              ? (parsedPrice * 12).toFixed(2)
                              : parsedPrice.toFixed(2)}
                          </span>
                          <span>
                            (save $
                            {period === "yearly"
                              ? (
                                  parsedPrice * 12 -
                                  displayPrice.value * 12
                                ).toFixed(2)
                              : (parsedPrice - displayPrice.value).toFixed(2)}
                            )
                          </span>
                        </div>
                      </div>
                      {/* {coupon.query.data?.coupon && period === "yearly" && (
                        <div className="text-sm font-medium">
                          Pay for 3 months, get 12
                        </div>
                      )} */}
                    </>
                  ) : (
                    <>
                      <h2 className="text-3xl font-bold">
                        {`$${displayPrice.value.toFixed(2)}`}
                        <span className="invisible block text-lg">
                          {`$${displayPrice.value.toFixed(2)}`}
                        </span>
                      </h2>
                    </>
                  )}
                </div>
                {(!coupon.query.data?.coupon || period !== "yearly") && (
                  <p className="flex flex-col text-sm text-foreground/80">
                    <span>per month</span>
                    <span>billed {period}</span>
                  </p>
                )}
              </div>
            )}
          </div>
          {plan.name === "Free Plan" && (
            <p className="mb-8 h-[calc(2.5*var(--text-sm))] text-sm text-foreground/80">
              {plan.description}
            </p>
          )}
        </div>
        {plan.name === "Enterprise" ? (
          <h4 className="text-md invisible block font-bold">Enterprise</h4>
        ) : (
          ""
        )}
        {variant === "default" ? (
          <AnimatedSignInButton
            to={coupon.code === "back2school" ? "/auth/signup" : "/auth/signin"}
            search={(s: any) => ({
              redirect: "/pricing",
              redirectSearch: s,
            })}
            disabled={isCurrentPlan || isFree || hasForeignPlan}
            buyPlan={() => buyPlan.mutate()}
            isCurrentPlan={isCurrentPlan}
            displayPrice={displayPrice}
            isLoggedOut={isLoggedOut}
          />
        ) : (
          <a
            className={cn(
              buttonVariants({ variant: "secondary" }),
              "h-12 w-full",
            )}
            href="https://polygraf.ai/contact"
          >
            Schedule a Demo
          </a>
        )}
      </div>

      <div>
        {planDetail?.tokens && planDetail.words && (
          <ul className="mb-4 space-y-4">
            <li className="flex items-center gap-2">
              <span>Number of monthly tokens: </span>
              <span className="font-bold text-primary">
                {planDetail.tokens}
              </span>
              <img
                src="/icons/p-token.svg"
                alt="Token Icon"
                className="h-5 w-5"
              />
            </li>
          </ul>
        )}
        {plan.features.available_services.length > 0 && (
          <h4 className="mb-4 text-sm font-semibold text-gray-500">FEATURES</h4>
        )}
        <ul className="space-y-4">
          {planDetail?.features.map((feature) => (
            <li key={feature} className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 flex-shrink-0 text-gray-700" />
              <span className="text-ellipsis">{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function FreePlanExpiredModal() {
  const expired = Route.useSearch({ select: (s) => s.freePlanExpired });
  return (
    <Dialog defaultOpen={expired}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Your Free Plan has expired</DialogTitle>
        </DialogHeader>
        Please purchase a plan to continue using Polygraf AI
      </DialogContent>
    </Dialog>
  );
}
